import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import {
  AiPart,
  AllowanceExceededError,
  callAi,
  normaliseMimeType,
} from "../services/aiClient";
import { useAuth } from "../contexts/AuthContext";
import {
  DEFAULT_UNITS_IMPERIAL,
  DEFAULT_UNITS_METRIC,
  getDefaultUnit,
} from "../utils/unitUtils";
import { activityService } from "../services/activityService";
import { GlobalActivityService } from "../services/globalActivityService";
import { trainingTypeService } from "../services/trainingTypeService";
import { TrainingType, ActivityType, ActivityVariable } from "../types/activity";
import { ActivityMeasurement } from "../types/post";
import { sessionService } from "../services/sessionService";
import MasterSessionDetails from "../components/sessions/MasterSessionDetails";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  MicrophoneIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  PaperClipIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import peakIconBlack from "../assets/icon-black.png";
import { useSubscription } from "../providers/SubscriptionProvider";
import ComingSoon from "../components/ComingSoon";

const SESSION_BUILDER_MODEL = "gemini-3-flash-preview";
// Leaves room for the model's thinking, which counts toward the reply limit.
const SESSION_BUILDER_MAX_OUTPUT_TOKENS = 16_384;

type BuilderStep = "input" | "processing" | "review" | "complete";
type ActiveInputType = "text" | "voice" | "image" | "audio" | null;
type SuggestedActivityDraft = {
  id: string;
  name: string;
  description: string;
  variableNames: string;
  accepted: boolean;
  similarTo?: string;
  reason?: string;
};

const SessionBuilder: React.FC = () => {
  const { currentUser, userData } = useAuth();
  const { isPremium } = useSubscription();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [availableActivities, setAvailableActivities] = useState<
    ActivityType[]
  >([]);
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateTrainingTypeID, setTemplateTrainingTypeID] = useState("");
  const [hasGeneratedTemplate, setHasGeneratedTemplate] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [isTranscribingAudio, setIsTranscribingAudio] = useState(false);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [isPreparingActivities, setIsPreparingActivities] = useState(false);
  const [leaveMeasurementsBlank, setLeaveMeasurementsBlank] = useState(true);
  const [onlyUseExistingActivities, setOnlyUseExistingActivities] =
    useState(true);
  const [pendingSessionInput, setPendingSessionInput] = useState("");
  const [suggestedActivities, setSuggestedActivities] = useState<
    SuggestedActivityDraft[]
  >([]);
  const [isSavingSuggestedActivities, setIsSavingSuggestedActivities] =
    useState(false);
  const [editingSuggestedActivityId, setEditingSuggestedActivityId] = useState<
    string | null
  >(null);
  const [editSuggestedName, setEditSuggestedName] = useState("");
  const [editSuggestedDescription, setEditSuggestedDescription] = useState("");
  const [editSuggestedVariables, setEditSuggestedVariables] = useState("");
  const [editSuggestedSimilarTo, setEditSuggestedSimilarTo] = useState("");
  const [editSuggestedAccepted, setEditSuggestedAccepted] = useState(true);
  const [plannedMeasurements, setPlannedMeasurements] = useState<
    ActivityMeasurement[]
  >([]);

  // Modal UX state - check URL param for auto-open
  const [isModalOpen, setIsModalOpen] = useState(() => {
    return searchParams.get("open") === "true";
  });
  const [currentStep, setCurrentStep] = useState<BuilderStep>("input");
  const [activeInputType, setActiveInputType] = useState<ActiveInputType>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<BlobPart[]>([]);
  const recordingMimeTypeRef = useRef<string>("audio/webm");
  const activePointerIdRef = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Runs one session builder model call through aiProxy, which checks the
  // athlete's monthly allowance and records the usage.
  const runModel = async (parts: AiPart[], json: boolean): Promise<string> => {
    const result = await callAi({
      feature: "sessionBuilder",
      model: SESSION_BUILDER_MODEL,
      contents: [{ role: "user", parts }],
      generationConfig: {
        maxOutputTokens: SESSION_BUILDER_MAX_OUTPUT_TOKENS,
        ...(json ? { json: true } : {}),
      },
    });
    return result.text.trim();
  };
  const inlinePart = (mimeType: string, data: string): AiPart => ({
    inlineData: { mimeType: normaliseMimeType(mimeType), data },
  });
  const aiErrorMessage = (error: unknown, fallback: string) =>
    error instanceof AllowanceExceededError ? error.message : fallback;

  const parseModelJson = (rawText: string): Record<string, unknown> => {
    const trimmed = rawText.trim();

    // Best case: already valid JSON object
    try {
      return JSON.parse(trimmed);
    } catch {
      // Continue to fallback parsing
    }

    // Common case: wrapped in markdown code fences
    const withoutFences = trimmed
      .replace(/^```json\s*/i, "")
      .replace(/^```\s*/i, "")
      .replace(/\s*```$/, "")
      .trim();

    try {
      return JSON.parse(withoutFences);
    } catch {
      // Continue to substring extraction
    }

    // Last attempt: parse the first JSON object found in text
    const firstBrace = withoutFences.indexOf("{");
    const lastBrace = withoutFences.lastIndexOf("}");
    if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
      const candidate = withoutFences.slice(firstBrace, lastBrace + 1);
      return JSON.parse(candidate);
    }

    throw new Error("Model response is not valid JSON");
  };

  const isInvalidInputResponse = (rawText: string): boolean => {
    const normalized = rawText.trim().replace(/^"+|"+$/g, "");
    return normalized === "Invalid Input";
  };

  const loadAvailableActivities = async (): Promise<ActivityType[]> => {
    if (!currentUser?.uid) {
      setAvailableActivities([]);
      return [];
    }

    try {
      setIsLoadingActivities(true);

      const userActivities = await activityService.fetchActivityTypes(
        currentUser.uid,
      );

      const packIds = userData?.adminActivityCatIDs || [];
      let packActivities: ActivityType[] = [];

      if (packIds.length > 0) {
        const globalTypes =
          await GlobalActivityService.fetchActivityTypesForCategories(packIds);
        packActivities = globalTypes.map((gt) => ({
          id: gt.id,
          name: gt.name,
          description: gt.description,
          variables: gt.variables,
          image: gt.image,
          isFromPack: true,
          categoryId: gt.categoryId,
        }));
      }

      // Match Planner behavior: append unique pack activities by name.
      const userNames = new Set(
        userActivities.map((a) => a.name.toLowerCase()),
      );
      const uniquePackActivities = packActivities.filter(
        (pa) => !userNames.has(pa.name.toLowerCase()),
      );
      const merged = [...userActivities, ...uniquePackActivities];
      setAvailableActivities(merged);
      return merged;
    } catch (activitiesError) {
      console.error("Error loading available activities:", activitiesError);
      setAvailableActivities([]);
      return [];
    } finally {
      setIsLoadingActivities(false);
    }
  };

  useEffect(() => {
    loadAvailableActivities();
  }, [currentUser?.uid, userData?.adminActivityCatIDs]);

  useEffect(() => {
    const fetchTrainingTypes = async () => {
      try {
        const types = await trainingTypeService.fetchTrainingTypesForSports(
          userData?.trainingSportIDs,
          userData?.customTrainingTypes,
        );
        setTrainingTypes(types);
      } catch (fetchError) {
        console.error("Error fetching training types:", fetchError);
      }
    };

    fetchTrainingTypes();
  }, [userData?.trainingSportIDs, userData?.customTrainingTypes]);

  const buildPrompt = (userInput: string, activities: ActivityType[]) => {
    const isImperial = Boolean(userData?.isImperial);
    const measurementSystem = isImperial ? "Imperial" : "Metric";
    const defaultUnits = isImperial
      ? DEFAULT_UNITS_IMPERIAL
      : DEFAULT_UNITS_METRIC;
    const measurementValueInstruction = leaveMeasurementsBlank
      ? "- Measurement value rule: set every variables[].measurement to null (clean slate mode is ON)."
      : "- Measurement value rule: if the user provides a clear numeric value (weight/reps/time/distance), include it; otherwise set variables[].measurement to null.";

    const activitiesForPrompt = activities
      .filter((activity) => activity.id)
      .map((activity) => ({
        ActivityTypeID: activity.id!,
        ...(activity.isFromPack && activity.categoryId
          ? { globalCategoryID: activity.categoryId }
          : {}),
        name: activity.name,
        variables: (activity.variables || []).map((variable) =>
          typeof variable === "string"
            ? { name: variable }
            : { name: variable.name, defaultUnit: variable.defaultUnit },
        ),
      }));

    return [
      `Make a session plan based on the following user input: '${userInput}'`,
      "",
      `User measurement system: ${measurementSystem}`,
      "CRITICAL UNIT INSTRUCTION: You MUST apply the user's measurement system consistently across the entire session.",
      "Default units by variable for this user (treat these as mandatory unless the user explicitly requests different units):",
      JSON.stringify(defaultUnits, null, 2),
      "",
      "Available activities (use only these):",
      JSON.stringify(activitiesForPrompt, null, 2),
      "",
      "Return ONLY valid JSON. No markdown, no code fences, and no extra text.",
      "Your response MUST start with { and end with }.",
      "The JSON must be compatible with this TypeScript shape:",
      '{ "name": string, "description": string, "ActivityMeasurementList"?: Array<{ "ActivityTypeID": string, "globalCategoryID"?: string, "variables": Array<{ "name": string, "measurement": number | null, "unit"?: string }>, "timeLogged"?: string, "activityIndex"?: number }> }',
      "Rules:",
      "- Use only ActivityTypeID values from the available activities list.",
      "- If an activity includes globalCategoryID in the available list, include that same globalCategoryID in the measurement.",
      "- Each measurement.variables array must only use variable names defined for that activity.",
      measurementValueInstruction,
      "- variables[].unit is REQUIRED for every variable entry (do not omit it).",
      `- All units MUST follow the user's ${measurementSystem} system.`,
      "- Do NOT mix metric and imperial units in the same output.",
      "- Use the provided default unit map for each variable unless the user explicitly requests a different unit for that variable.",
      "- CRITICAL SET FORMAT RULE: One activity entry equals exactly one set.",
      "- To represent multiple sets, you MUST repeat the same ActivityTypeID as separate consecutive entries (e.g., activity1, activity1, activity1, then activity2, activity2).",
      "- Do NOT collapse multiple sets into a single entry.",
      "- Do NOT use custom fields, notes, or variable hacks to represent set counts.",
      "- If the user input is nonsense, unclear gibberish, or not a valid session request, return exactly: Invalid Input",
      "- Output exactly one JSON object and nothing else.",
    ].join("\n");
  };

  const parseSuggestedActivities = (raw: unknown): SuggestedActivityDraft[] => {
    const container =
      raw && typeof raw === "object" && !Array.isArray(raw)
        ? (raw as Record<string, unknown>)
        : null;
    const sourceArray = Array.isArray(raw)
      ? raw
      : Array.isArray(container?.suggestedActivities)
        ? container?.suggestedActivities
        : Array.isArray(container?.activities)
          ? container?.activities
          : null;

    if (!sourceArray || sourceArray.length === 0) {
      return [];
    }

    const drafts: SuggestedActivityDraft[] = [];
    sourceArray.forEach((item, index) => {
      if (!item || typeof item !== "object") return;

      const typed = item as Record<string, unknown>;
      const name =
        typeof typed.name === "string"
          ? typed.name.trim()
          : `Activity ${index + 1}`;
      const description =
        typeof typed.description === "string" ? typed.description.trim() : "";
      const similarTo =
        typeof typed.similarTo === "string"
          ? typed.similarTo.trim()
          : typeof typed.similar_activity === "string"
            ? typed.similar_activity.trim()
            : undefined;
      const reason =
        typeof typed.reason === "string" ? typed.reason.trim() : undefined;
      const rawVariables = Array.isArray(typed.variables)
        ? typed.variables
        : Array.isArray(typed.variableNames)
          ? typed.variableNames
          : [];

      const variableNames = Array.from(
        new Set(
          rawVariables
            .map((v) => (typeof v === "string" ? v.trim() : ""))
            .filter(Boolean),
        ),
      );

      const normalizedVariables =
        variableNames.length > 0 ? variableNames : ["Time"];

      if (!name) return;
      drafts.push({
        id: `suggested-${Date.now()}-${index}`,
        name,
        description,
        variableNames: normalizedVariables.join(", "),
        accepted: true,
        ...(similarTo ? { similarTo } : {}),
        ...(reason ? { reason } : {}),
      });
    });

    return drafts;
  };

  const buildSuggestedActivitiesPrompt = (
    userInput: string,
    existingAccessibleActivities: ActivityType[],
  ) => {
    const existingActivities = existingAccessibleActivities
      .filter((activity) => activity.id)
      .map((activity) => ({
        ActivityTypeID: activity.id!,
        source: activity.isFromPack ? "pack" : "custom",
        ...(activity.categoryId
          ? { globalCategoryID: activity.categoryId }
          : {}),
        name: activity.name,
        description: activity.description,
        variables: (activity.variables || []).map((variable) =>
          typeof variable === "string" ? variable : variable.name,
        ),
      }));

    return [
      `The user requested this session: '${userInput}'`,
      "",
      "You are deciding whether any NEW activity types should be created before generating the session.",
      "These are activities already accessible to this user, including custom activities and enabled activity-pack/global-category activities.",
      "CRITICAL MATCHING RULE: Only treat activities as similar if the core movement/exercise is essentially the same.",
      "Do NOT match based on generic words, shared equipment, or broad category alone.",
      "If the requested movement is materially different, you MUST suggest a new activity.",
      "",
      "Existing accessible activities:",
      JSON.stringify(existingActivities, null, 2),
      "",
      "Return ONLY valid JSON (no markdown).",
      "If no new activities are needed, return exactly: null",
      'If suggestions are needed, return: {"suggestedActivities":[{"name":string,"description":string,"variables":string[],"similarTo"?:string,"reason":string}]}',
      "Rules for suggestions:",
      "- Suggest only clearly necessary activities that are not reasonably covered by existing accessible activities.",
      "- Similar means near-equivalent movement intent and pattern (close variants are okay).",
      '- Example similar: "Fast back squat" is similar enough to "Back Squat" (reuse existing).',
      '- Example NOT similar: "Hang Clean" is NOT similar to "Bended Lateral Walk" (create new "Hang Clean").',
      "- When uncertain between a weak match vs a new activity, prefer creating a new activity.",
      "- Include similarTo with the closest existing activity name when one was considered; omit it if none.",
      "- Keep variable names concise (e.g., Distance, Time, Reps, Weight, Rest, Intensity).",
      "- Keep descriptions short and practical.",
      "- Prefer 1-3 suggestions total.",
    ].join("\n");
  };

  const suggestActivitiesToCreate = async (
    userInput: string,
    existingAccessibleActivities: ActivityType[],
  ): Promise<SuggestedActivityDraft[]> => {
    const prompt = buildSuggestedActivitiesPrompt(
      userInput,
      existingAccessibleActivities,
    );
    const rawResponse = await runModel([{ text: prompt }], true);

    if (!rawResponse || rawResponse === "null") {
      return [];
    }

    const parsed = parseModelJson(rawResponse);
    return parseSuggestedActivities(parsed);
  };

  const generateSessionPlan = async (
    userInput: string,
    activitiesForPrompt: ActivityType[],
  ) => {
    if (!userInput || isLoading || isLoadingActivities) return;
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    setHasGeneratedTemplate(false);

    try {
      const prompt = buildPrompt(userInput, activitiesForPrompt);
      const llmResponse = await runModel([{ text: prompt }], true);

      if (isInvalidInputResponse(llmResponse)) {
        setHasGeneratedTemplate(false);
        setPlannedMeasurements([]);
        setTemplateName("");
        setTemplateDescription("");
        setTemplateTrainingTypeID("");
        setError("Invalid Input");
        setCurrentStep("input"); // Go back to input on error
        setActiveInputType(null);
        return;
      }

      const parsedJson = parseModelJson(llmResponse);

      const name =
        typeof parsedJson.name === "string" && parsedJson.name.trim()
          ? parsedJson.name.trim()
          : `Session plan for ${userInput}`;
      const description =
        typeof parsedJson.description === "string"
          ? parsedJson.description
          : "";
      const measurementsRaw = Array.isArray(parsedJson.ActivityMeasurementList)
        ? parsedJson.ActivityMeasurementList
        : [];

      const safeMeasurements: ActivityMeasurement[] = measurementsRaw
        .map((measurement) => {
          if (
            !measurement ||
            typeof measurement !== "object" ||
            typeof (measurement as { ActivityTypeID?: unknown })
              .ActivityTypeID !== "string"
          ) {
            return null;
          }

          const typedMeasurement = measurement as {
            ActivityTypeID: string;
            globalCategoryID?: unknown;
            activityIndex?: unknown;
            timeLogged?: unknown;
            variables?: unknown;
          };

          const variablesRaw = Array.isArray(typedMeasurement.variables)
            ? typedMeasurement.variables
            : [];

          const safeVariables: ActivityVariable[] = variablesRaw
            .map((variable) => {
              if (
                !variable ||
                typeof variable !== "object" ||
                typeof (variable as { name?: unknown }).name !== "string"
              ) {
                return null;
              }

              const typedVariable = variable as {
                name: string;
                measurement?: unknown;
                unit?: unknown;
              };

              return {
                name: typedVariable.name,
                measurement:
                  !leaveMeasurementsBlank &&
                  typeof typedVariable.measurement === "number"
                    ? typedVariable.measurement
                    : null,
                ...(typeof typedVariable.unit === "string"
                  ? { unit: typedVariable.unit }
                  : {}),
              };
            })
            .filter(
              (variable): variable is ActivityVariable => variable !== null,
            );

          const normalizedMeasurement: ActivityMeasurement = {
            ActivityTypeID: typedMeasurement.ActivityTypeID,
            variables: safeVariables,
          };

          if (typeof typedMeasurement.globalCategoryID === "string") {
            normalizedMeasurement.globalCategoryID =
              typedMeasurement.globalCategoryID;
          }
          if (typeof typedMeasurement.activityIndex === "number") {
            normalizedMeasurement.activityIndex =
              typedMeasurement.activityIndex;
          }
          if (typeof typedMeasurement.timeLogged === "string") {
            normalizedMeasurement.timeLogged = typedMeasurement.timeLogged;
          }

          return normalizedMeasurement;
        })
        .filter(
          (measurement): measurement is ActivityMeasurement =>
            measurement !== null,
        );

      setTemplateName(name);
      setTemplateDescription(description);
      setTemplateTrainingTypeID("");
      setPlannedMeasurements(safeMeasurements);
      setHasGeneratedTemplate(true);

      // Show complete step briefly, then close modal
      setCurrentStep("complete");
      setActiveInputType(null);
      setTimeout(() => {
        setIsModalOpen(false);
        setCurrentStep("input");
      }, 1500);
    } catch (submitError) {
      console.error("Error generating session plan:", submitError);
      setError(
        aiErrorMessage(
          submitError,
          "Failed to get valid JSON response. Please try again.",
        ),
      );
      setCurrentStep("input"); // Go back to input on error
      setActiveInputType(null);
    } finally {
      setIsLoading(false);
    }
  };

  const runPreCreationFlowIfNeeded = async (
    userInput: string,
  ): Promise<{
    shouldContinue: boolean;
    activitiesForPrompt: ActivityType[];
  }> => {
    const latestActivities = await loadAvailableActivities();

    if (onlyUseExistingActivities) {
      return { shouldContinue: true, activitiesForPrompt: latestActivities };
    }

    try {
      setIsPreparingActivities(true);
      const suggestions = await suggestActivitiesToCreate(
        userInput,
        latestActivities,
      );

      if (suggestions.length === 0) {
        return { shouldContinue: true, activitiesForPrompt: latestActivities };
      }

      setSuggestedActivities(suggestions);
      setPendingSessionInput(userInput);
      setCurrentStep("review");
      setActiveInputType(null);
      return { shouldContinue: false, activitiesForPrompt: latestActivities };
    } catch (error) {
      console.error("Error suggesting activities:", error);
      if (error instanceof AllowanceExceededError) {
        setError(error.message);
        setCurrentStep("input");
        setActiveInputType(null);
        return { shouldContinue: false, activitiesForPrompt: latestActivities };
      }
      setError("Could not suggest new exercises. You can still continue.");
      return { shouldContinue: true, activitiesForPrompt: latestActivities };
    } finally {
      setIsPreparingActivities(false);
    }
  };

  const startSessionGeneration = async (userInput: string) => {
    const trimmed = userInput.trim();
    if (!trimmed) return;

    setError(null);
    setSuccessMessage(null);
    const preCheck = await runPreCreationFlowIfNeeded(trimmed);
    if (!preCheck.shouldContinue) {
      return;
    }

    await generateSessionPlan(trimmed, preCheck.activitiesForPrompt);
  };

  const updateSuggestedActivity = (
    id: string,
    updates: Partial<SuggestedActivityDraft>,
  ) => {
    setSuggestedActivities((prev) =>
      prev.map((activity) =>
        activity.id === id ? { ...activity, ...updates } : activity,
      ),
    );
  };

  const parseVariableNamesInput = (value: string): string[] =>
    Array.from(
      new Set(
        value
          .split(",")
          .map((name) => name.trim())
          .filter(Boolean),
      ),
    );

  const handleOpenSuggestedActivityEditor = (
    activity: SuggestedActivityDraft,
  ) => {
    setEditingSuggestedActivityId(activity.id);
    setEditSuggestedName(activity.name);
    setEditSuggestedDescription(activity.description);
    setEditSuggestedVariables(activity.variableNames);
    setEditSuggestedSimilarTo(activity.similarTo || "");
    setEditSuggestedAccepted(activity.accepted);
  };

  const handleCloseSuggestedActivityEditor = () => {
    setEditingSuggestedActivityId(null);
    setEditSuggestedName("");
    setEditSuggestedDescription("");
    setEditSuggestedVariables("");
    setEditSuggestedSimilarTo("");
    setEditSuggestedAccepted(true);
  };

  const handleSaveSuggestedActivityEditor = () => {
    if (!editingSuggestedActivityId) return;
    updateSuggestedActivity(editingSuggestedActivityId, {
      name: editSuggestedName,
      description: editSuggestedDescription,
      variableNames: editSuggestedVariables,
      similarTo: editSuggestedSimilarTo.trim() || undefined,
      accepted: editSuggestedAccepted,
    });
    handleCloseSuggestedActivityEditor();
  };

  const handleSkipSuggestedActivities = async () => {
    const inputToUse = pendingSessionInput || input;
    setSuggestedActivities([]);
    setPendingSessionInput("");
    handleCloseSuggestedActivityEditor();
    setCurrentStep("processing");
    const refreshedActivities = await loadAvailableActivities();
    await generateSessionPlan(inputToUse, refreshedActivities);
  };

  const handleSaveSuggestedActivitiesAndContinue = async () => {
    if (!currentUser?.uid) {
      setError("You must be logged in to create exercises.");
      return;
    }

    const selected = suggestedActivities.filter(
      (activity) => activity.accepted,
    );
    if (selected.length === 0) {
      await handleSkipSuggestedActivities();
      return;
    }

    try {
      setIsSavingSuggestedActivities(true);
      setError(null);

      const isImperial = Boolean(userData?.isImperial);

      for (const activity of selected) {
        const variableNames = parseVariableNamesInput(activity.variableNames);

        if (!activity.name.trim() || variableNames.length === 0) {
          continue;
        }

        await activityService.createActivityType(currentUser.uid, {
          name: activity.name.trim(),
          description: activity.description.trim(),
          variables: variableNames.map((name) => ({
            name,
            defaultUnit: getDefaultUnit(name, isImperial),
          })),
        });
      }

      setSuggestedActivities([]);
      handleCloseSuggestedActivityEditor();
      const inputToUse = pendingSessionInput || input;
      setPendingSessionInput("");
      setCurrentStep("processing");

      // Always refresh activity types before the main session prompt.
      const refreshedActivities = await loadAvailableActivities();
      await generateSessionPlan(inputToUse, refreshedActivities);
    } catch (error) {
      console.error("Error saving suggested activities:", error);
      setError("Failed to save suggested exercises. Please try again.");
      setCurrentStep("review");
    } finally {
      setIsSavingSuggestedActivities(false);
    }
  };

  const stopAudioLevelMonitoring = () => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {
        // no-op
      });
      audioContextRef.current = null;
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }

    analyserRef.current = null;
    setAudioLevel(0);
  };

  const startAudioLevelMonitoring = (stream: MediaStream) => {
    try {
      const AudioContextCtor =
        window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextCtor();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const updateLevel = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteTimeDomainData(dataArray);

        let sumSquares = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const centered = (dataArray[i] - 128) / 128;
          sumSquares += centered * centered;
        }
        const rms = Math.sqrt(sumSquares / dataArray.length);
        const normalizedLevel = Math.min(1, rms * 5);
        setAudioLevel(normalizedLevel);

        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };

      updateLevel();
    } catch (levelError) {
      console.error("Failed to start audio level monitoring:", levelError);
      setAudioLevel(0);
    }
  };

  const blobToBase64 = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== "string") {
          reject(new Error("Failed to read audio file"));
          return;
        }
        const base64 = result.split(",")[1];
        if (!base64) {
          reject(new Error("Invalid audio file data"));
          return;
        }
        resolve(base64);
      };
      reader.onerror = () => reject(new Error("Failed to read audio file"));
      reader.readAsDataURL(blob);
    });

  const transcribeAudioAndGenerate = async (audioBlob: Blob) => {
    if (!audioBlob || audioBlob.size === 0) {
      setError("No audio captured. Please try again.");
      return;
    }

    const maxBytes = 10 * 1024 * 1024;
    if (audioBlob.size > maxBytes) {
      setError("Audio is too large. Please keep recordings under 10MB.");
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      setIsTranscribingAudio(true);

      const base64Audio = await blobToBase64(audioBlob);
      const mimeType = audioBlob.type || "audio/webm";
      const transcriptionPrompt =
        "Transcribe the speech from this audio. Return only plain text with no extra formatting.";

      const transcript = await runModel(
        [{ text: transcriptionPrompt }, inlinePart(mimeType, base64Audio)],
        false,
      );
      if (!transcript) {
        setError("Could not transcribe audio. Please try again.");
        return;
      }

      setInput(transcript);
      await startSessionGeneration(transcript);
    } catch (audioError) {
      console.error("Error transcribing audio:", audioError);
      setError(aiErrorMessage(audioError, "Failed to process audio. Please try again."));
    } finally {
      setIsTranscribingAudio(false);
    }
  };

  const handleAudioFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    // Reset input so same file can be selected again.
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("audio/")) {
      setError("Please upload a valid audio file.");
      return;
    }

    // Keep payload size practical for inline requests.
    const maxBytes = 10 * 1024 * 1024;
    if (file.size > maxBytes) {
      setError("Audio file is too large. Please upload a file under 10MB.");
      return;
    }

    if (
      isLoading ||
      isLoadingActivities ||
      isSavingTemplate ||
      isTranscribingAudio
    ) {
      return;
    }

    await transcribeAudioAndGenerate(file);
  };

  const handleImageFileSelected = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    // Reset input so same file can be selected again.
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please upload a valid image file.");
      return;
    }

    const maxBytes = 10 * 1024 * 1024;
    if (file.size > maxBytes) {
      setError("Image file is too large. Please upload a file under 10MB.");
      return;
    }

    if (
      isLoading ||
      isLoadingActivities ||
      isSavingTemplate ||
      isTranscribingAudio ||
      isProcessingImage
    ) {
      return;
    }

    try {
      setError(null);
      setSuccessMessage(null);
      setIsProcessingImage(true);

      const base64Image = await blobToBase64(file);
      const extractionPrompt = [
        "Read this image and extract the user's intended training session request.",
        "Return only plain text describing the request.",
        "If the image does not contain a clear, valid session request, return exactly: Invalid Input",
      ].join("\n");

      const extractedText = await runModel(
        [{ text: extractionPrompt }, inlinePart(file.type, base64Image)],
        false,
      );
      if (!extractedText || isInvalidInputResponse(extractedText)) {
        setError("Invalid Input");
        return;
      }

      setInput(extractedText);
      await startSessionGeneration(extractedText);
    } catch (imageError) {
      console.error("Error processing image:", imageError);
      setError(aiErrorMessage(imageError, "Failed to process image. Please try again."));
    } finally {
      setIsProcessingImage(false);
    }
  };

  // Combined file handler for the unified paperclip button
  const handleFileAttachment = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const maxBytes = 10 * 1024 * 1024;
    if (file.size > maxBytes) {
      setError("File is too large. Please upload a file under 10MB.");
      return;
    }

    if (isProcessingAnyInput) return;

    if (file.type.startsWith("image/")) {
      setActiveInputType("image");
      setCurrentStep("processing");
      try {
        setError(null);
        setSuccessMessage(null);
        setIsProcessingImage(true);
        const base64Image = await blobToBase64(file);
        const extractionPrompt = [
          "Read this image and extract the user's intended training session request.",
          "Return only plain text describing the request.",
          "If the image does not contain a clear, valid session request, return exactly: Invalid Input",
        ].join("\n");
        const extractedText = await runModel(
          [{ text: extractionPrompt }, inlinePart(file.type, base64Image)],
          false,
        );
        if (!extractedText || isInvalidInputResponse(extractedText)) {
          setError("Invalid Input");
          return;
        }
        setInput(extractedText);
        await startSessionGeneration(extractedText);
      } catch (imgErr) {
        console.error("Error processing image:", imgErr);
        setError(aiErrorMessage(imgErr, "Failed to process image. Please try again."));
      } finally {
        setIsProcessingImage(false);
      }
    } else if (file.type.startsWith("audio/")) {
      setActiveInputType("audio");
      setCurrentStep("processing");
      await transcribeAudioAndGenerate(file);
    } else {
      setError("Please upload an image or audio file.");
    }
  };

  const handleVoiceStart = async () => {
    if (
      isLoading ||
      isLoadingActivities ||
      isSavingTemplate ||
      isRecording ||
      isTranscribingAudio
    ) {
      return;
    }

    if (
      typeof window === "undefined" ||
      !navigator.mediaDevices?.getUserMedia
    ) {
      setError(
        "Voice input is not supported in this browser. Use Upload Audio.",
      );
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      setError(
        "Voice input is not supported in this browser. Use Upload Audio.",
      );
      return;
    }

    setError(null);
    setSuccessMessage(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const preferredTypes = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      const selectedType = preferredTypes.find(
        (type) =>
          MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type),
      );
      const recorder = selectedType
        ? new MediaRecorder(stream, { mimeType: selectedType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current = recorder;
      recordingMimeTypeRef.current = selectedType || "audio/webm";
      recordedChunksRef.current = [];

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = (event: any) => {
        console.error("Voice recorder error:", event);
        setIsRecording(false);
        stopAudioLevelMonitoring();
        setError("Voice capture failed. Please try again.");
      };

      recorder.onstop = async () => {
        setIsRecording(false);
        mediaRecorderRef.current = null;
        const mimeType =
          recordedChunksRef.current[0] instanceof Blob &&
          (recordedChunksRef.current[0] as Blob).type
            ? (recordedChunksRef.current[0] as Blob).type
            : recordingMimeTypeRef.current;
        const audioBlob = new Blob(recordedChunksRef.current, {
          type: mimeType,
        });
        recordedChunksRef.current = [];
        stopAudioLevelMonitoring();
        await transcribeAudioAndGenerate(audioBlob);
      };

      startAudioLevelMonitoring(stream);
      recorder.start(200);
      setIsRecording(true);
    } catch (voiceError) {
      console.error("Error starting voice recording:", voiceError);
      setIsRecording(false);
      stopAudioLevelMonitoring();
      setError("Unable to start voice input.");
    }
  };

  const handleVoiceStop = () => {
    if (!isRecording) return;
    try {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    } catch (voiceError) {
      console.error("Error stopping voice recording:", voiceError);
      setIsRecording(false);
      stopAudioLevelMonitoring();
    }
  };

  const handleSubmit = async () => {
    const userInput = input.trim();
    if (!userInput) return;
    await startSessionGeneration(userInput);
  };

  useEffect(() => {
    return () => {
      try {
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
        }
      } catch {
        // no-op cleanup
      }
      stopAudioLevelMonitoring();
    };
  }, []);

  const handleSaveTemplate = async () => {
    if (!currentUser?.uid) {
      setError("You must be logged in to save a template.");
      return;
    }
    if (!templateName.trim()) {
      setError("Please enter a template name.");
      return;
    }
    if (plannedMeasurements.length === 0) {
      setError("Please add at least one exercise.");
      return;
    }

    try {
      setIsSavingTemplate(true);
      setError(null);
      setSuccessMessage(null);

      await sessionService.createSessionTemplate(currentUser.uid, {
        name: templateName.trim(),
        description: templateDescription.trim(),
        ActivityMeasurementList: plannedMeasurements,
        ...(templateTrainingTypeID.trim()
          ? { trainingTypeID: templateTrainingTypeID.trim() }
          : {}),
      });

      setSuccessMessage("Session template saved.");
      setIsSavingTemplate(false);

      // Navigate to planner after successful save
      navigate("/planner");
    } catch (saveError) {
      console.error("Error saving session template:", saveError);
      setError("Failed to save template. Please try again.");
      setIsSavingTemplate(false);
    }
  };

  // Modal handlers
  const resetBuildSessionModalState = (options?: {
    keepMessages?: boolean;
  }) => {
    // Stop any active voice capture immediately
    try {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    } catch {
      // no-op
    }
    stopAudioLevelMonitoring();
    recordedChunksRef.current = [];
    mediaRecorderRef.current = null;
    activePointerIdRef.current = null;

    setCurrentStep("input");
    setActiveInputType(null);
    setInput("");
    setIsRecording(false);
    setPendingSessionInput("");
    setSuggestedActivities([]);
    handleCloseSuggestedActivityEditor();

    // Clear any in-flight UI states so the modal always opens "fresh"
    setIsLoading(false);
    setIsTranscribingAudio(false);
    setIsProcessingImage(false);
    setIsPreparingActivities(false);
    setIsSavingSuggestedActivities(false);
    setAudioLevel(0);

    if (!options?.keepMessages) {
      setError(null);
      setSuccessMessage(null);
    }

    // Clear file inputs (so selecting the same file again triggers onChange)
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (imageInputRef.current) imageInputRef.current.value = "";
    if (attachmentInputRef.current) attachmentInputRef.current.value = "";
  };

  const handleOpenModal = () => {
    resetBuildSessionModalState();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    // Ensure any voice capture is stopped when dismissing the modal
    resetBuildSessionModalState();
    setIsModalOpen(false);
    // When closing the builder modal, return to Sessions
    navigate("/sessions");
  };

  const handleBackToInput = () => {
    setCurrentStep("input");
    setActiveInputType(null);
    setError(null);
    setPendingSessionInput("");
    setSuggestedActivities([]);
    handleCloseSuggestedActivityEditor();
  };

  // Helper to check if we're in a processing state for any input
  const isProcessingAnyInput =
    isLoading ||
    isTranscribingAudio ||
    isProcessingImage ||
    isPreparingActivities ||
    isSavingSuggestedActivities;

  const getStepTitle = () => {
    switch (currentStep) {
      case "input":
        return "Build a Session";
      case "processing":
        return "Creating Session";
      case "review":
        return "Review Suggested Exercises";
      case "complete":
        return "Success!";
      default:
        return "Build a Session";
    }
  };

  const getStepSubtitle = () => {
    switch (currentStep) {
      case "input":
        return "Tell me what kind of session you want";
      case "processing":
        return "AI is generating your session...";
      case "review":
        return "Accept, reject, or edit activities before saving";
      case "complete":
        return "Your session template has been saved";
      default:
        return "";
    }
  };

  return (
    <>
      <div className="w-full pt-16">
        <Header />
        <div className="max-w-3xl mx-auto space-y-4">
          {/* Main page content - shows button to open modal or the review/results */}
          {!hasGeneratedTemplate ? (
            <div className="flex flex-col items-center justify-left py-16 space-y-6">
              <h1 className="text-2xl font-grotesk font-medium text-center">
                Session Builder
              </h1>
              <p className="text-base-content/60 text-center max-w-md">
                Let me help you create a training session template.
              </p>
              <button
                onClick={handleOpenModal}
                className="bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-full py-4 px-8 font-grotesk font-bold hover:opacity-90 transition-opacity shadow-lg"
              >
                Build a Session
              </button>
            </div>
          ) : (
            <>
              {/* Success message */}
              {successMessage && (
                <p className="text-sm text-green-600" role="status">
                  {successMessage}
                </p>
              )}

              {/* Error message */}
              {error && (
                <p className="text-sm text-red-500" role="alert">
                  {error}
                </p>
              )}

              {/* Template editing form */}
              <div className="border border-base-300 md:mt-8 mt-0 md:rounded-xl rounded-none mb-20">
                <div className="bg-base-100 md:rounded-xl rounded-none">
                  <div className="p-8 space-y-4 pb-0">
                    <div className="flex justify-between pb-4">
                      <div>
                        <button
                          onClick={() => {
                            setHasGeneratedTemplate(false);
                            setPlannedMeasurements([]);
                            setTemplateName("");
                            setTemplateDescription("");
                            setTemplateTrainingTypeID("");
                            setPendingSessionInput("");
                            setSuggestedActivities([]);
                            handleCloseSuggestedActivityEditor();
                          }}
                          className="flex-1 py-3 px-6 rounded-full bg-base-200 font-grotesk font-bold hover:bg-base-300 transition-colors flex items-center justify-center gap-2"
                        >
                          <ArrowLeftIcon className="h-4 w-4" />
                          Start Over
                        </button>
                      </div>
                      <div>
                        {/* Make this button open a text / audio prompt to further
                        improve the session ie add more sets or add less and add
                        some pre-populated data. */}
                        <button className="bg-gradient-to-br from-purple-600 to-blue-600 text-white flex-1 py-3 px-6 rounded-full font-grotesk font-bold transition-colors flex items-center justify-center gap-2">
                          Enhance
                        </button>
                      </div>
                    </div>
                    <div className="new-form-group-rounded">
                      <input
                        type="text"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        placeholder=" "
                        className="form-element-rounded"
                        disabled={isSavingTemplate}
                      />
                      <label>Template name</label>
                    </div>

                    <div className="new-form-group-rounded">
                      <select
                        value={templateTrainingTypeID}
                        onChange={(e) =>
                          setTemplateTrainingTypeID(e.target.value)
                        }
                        className="form-element-rounded appearance-none"
                        disabled={isSavingTemplate}
                      >
                        <option value="">None</option>
                        {trainingTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </select>
                      <label>Session Type (Optional)</label>
                    </div>

                    <div className="new-form-group-rounded">
                      <textarea
                        value={templateDescription}
                        onChange={(e) => setTemplateDescription(e.target.value)}
                        placeholder=" "
                        rows={3}
                        className="form-element-rounded resize-none"
                        disabled={isSavingTemplate}
                      />
                      <label>Notes</label>
                    </div>
                  </div>

                  <div className="bg-base-100">
                    <MasterSessionDetails
                      plannedMeasurements={plannedMeasurements}
                      onPlannedMeasurementsChange={setPlannedMeasurements}
                      loading={isSavingTemplate}
                      error={error}
                      onErrorChange={setError}
                      mode="planning"
                    />
                  </div>

                  <div className="flex gap-3 pb-8 px-8">
                    <button
                      onClick={handleSaveTemplate}
                      disabled={
                        isSavingTemplate ||
                        isLoading ||
                        !templateName.trim() ||
                        plannedMeasurements.length === 0
                      }
                      className="flex-1 bg-green-600 text-white rounded-full py-3 px-6 font-grotesk font-bold disabled:bg-green-200 disabled:cursor-not-allowed"
                    >
                      {isSavingTemplate ? "Saving..." : "Save Template"}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal for input */}
      <Transition appear show={isModalOpen}>
        <Dialog as="div" className="relative z-[60]" onClose={handleCloseModal}>
          <TransitionChild
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex h-full sm:min-h-full sm:items-center sm:justify-center sm:p-4">
              <TransitionChild
                enter="ease-out duration-200"
                enterFrom="opacity-0 sm:scale-95"
                enterTo="opacity-100 sm:scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 sm:scale-100"
                leaveTo="opacity-0 sm:scale-95"
              >
                <DialogPanel className="w-full h-full sm:h-auto sm:max-w-md transform overflow-hidden sm:rounded-2xl bg-base-100 shadow-xl transition-all flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between p-8 pb-0">
                    <div className="flex items-center gap-3">
                      {currentStep === "review" && (
                        <button
                          onClick={handleBackToInput}
                          className="p-2 -ml-2 hover:bg-base-200 rounded-full transition-colors"
                        >
                          <ArrowLeftIcon className="h-5 w-5 text-base-content/60" />
                        </button>
                      )}
                      <div className="h-10 w-10 rounded-full flex items-center justify-center">
                        <img
                          src={peakIconBlack}
                          alt="Peak"
                          className="h-10 w-10 dark:invert"
                        />
                      </div>
                    </div>
                    <button
                      onClick={handleCloseModal}
                      className="p-2 hover:bg-base-200 rounded-full transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5 text-base-content/60" />
                    </button>
                  </div>

                  {/* Content Area */}
                  <div className="flex-1 overflow-y-auto p-6 pt-8 pb-8">
                    {isPremium && (
                      <div className="px-4 pb-8">
                        <div className="flex items-center justify-between">
                          <DialogTitle className="text-2xl font-semibold text-base-content font-grotesk">
                            {getStepTitle()}
                          </DialogTitle>
                          <button
                            onClick={() => {
                              setInput("");
                              setError(null);
                              setSuccessMessage(null);
                              setActiveInputType(null);
                              setCurrentStep("input");
                              setHasGeneratedTemplate(false);
                              setPlannedMeasurements([]);
                              setTemplateName("");
                              setTemplateDescription("");
                              setTemplateTrainingTypeID("");
                            }}
                            className="p-2 hover:bg-base-200 rounded-full transition-colors"
                            title="Reset all inputs"
                          >
                            <ArrowPathIcon className="h-5 w-5 text-base-content/60" />
                          </button>
                        </div>
                        <p className="text-sm text-base-content/60">
                          {getStepSubtitle()}
                        </p>
                      </div>
                    )}

                    {/* Hidden file inputs */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={handleAudioFileSelected}
                    />
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageFileSelected}
                    />
                    <input
                      ref={attachmentInputRef}
                      type="file"
                      accept="image/*,audio/*"
                      className="hidden"
                      onChange={handleFileAttachment}
                    />

                    {/* Premium gate */}
                    {!isPremium && (
                      <div className="px-4 py-8">
                        <ComingSoon featureKey="sessionBuilder" />
                      </div>
                    )}

                    {/* Step 1: AI-style unified input */}
                    {isPremium && (currentStep === "input" ||
                      (currentStep === "processing" &&
                        activeInputType !== null)) && (
                      <div className="space-y-3 px-4">
                        {/* Unified input container */}
                        <div
                          className={`rounded-2xl bg-base-200 transition-all duration-300 ${
                            isProcessingAnyInput
                              ? "opacity-60 pointer-events-none"
                              : ""
                          }`}
                        >
                          <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (
                                e.key === "Enter" &&
                                !e.shiftKey &&
                                input.trim()
                              ) {
                                e.preventDefault();
                                setActiveInputType("text");
                                setCurrentStep("processing");
                                handleSubmit();
                              }
                            }}
                            placeholder="What sort of session do you want?"
                            className="w-full bg-transparent text-base-content placeholder:text-base-content/40 resize-none border-none outline-none focus:ring-0 p-4 pb-2 font-grotesk text-base min-h-[100px]"
                            disabled={isProcessingAnyInput}
                            rows={3}
                          />
                          <div className="flex items-center justify-between px-3 pb-3">
                            <button
                              type="button"
                              onClick={() =>
                                attachmentInputRef.current?.click()
                              }
                              disabled={isProcessingAnyInput}
                              className="p-2 rounded-full hover:bg-base-300 transition-colors text-base-content/40 hover:text-base-content/70"
                              title="Attach image or audio file"
                            >
                              <PaperClipIcon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2">
                              {/* Voice memo button */}
                              <button
                                type="button"
                                onPointerDown={(e) => {
                                  if (!e.isPrimary || isProcessingAnyInput)
                                    return;
                                  activePointerIdRef.current = e.pointerId;
                                  e.currentTarget.setPointerCapture?.(
                                    e.pointerId,
                                  );
                                  e.preventDefault();
                                  setActiveInputType("voice");
                                  void handleVoiceStart();
                                }}
                                onPointerUp={(e) => {
                                  if (
                                    activePointerIdRef.current !== null &&
                                    e.pointerId !== activePointerIdRef.current
                                  ) {
                                    return;
                                  }
                                  e.currentTarget.releasePointerCapture?.(
                                    e.pointerId,
                                  );
                                  activePointerIdRef.current = null;
                                  e.preventDefault();
                                  handleVoiceStop();
                                }}
                                onPointerCancel={(e) => {
                                  if (
                                    activePointerIdRef.current !== null &&
                                    e.pointerId !== activePointerIdRef.current
                                  ) {
                                    return;
                                  }
                                  activePointerIdRef.current = null;
                                  handleVoiceStop();
                                }}
                                disabled={
                                  isProcessingAnyInput &&
                                  activeInputType !== "voice"
                                }
                                className={`p-2 rounded-full transition-colors ${
                                  isRecording
                                    ? "bg-red-500 text-white animate-pulse"
                                    : "hover:bg-base-300 text-base-content/40 hover:text-base-content/70"
                                }`}
                                title="Hold to record voice memo"
                              >
                                <MicrophoneIcon className="h-5 w-5" />
                              </button>
                              {/* Send button - only visible when there's text */}
                              {input.trim() && !isProcessingAnyInput && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveInputType("text");
                                    setCurrentStep("processing");
                                    handleSubmit();
                                  }}
                                  className="p-2 rounded-full bg-base-content text-base-100 hover:opacity-80 transition-opacity"
                                  title="Generate session"
                                >
                                  <ArrowPathIcon className="h-5 w-5" />
                                </button>
                              )}
                            </div>
                          </div>
                          {/* Recording indicator */}
                          {isRecording && (
                            <div className="px-4 pb-3 flex items-center gap-2">
                              <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                              <span className="text-xs text-base-content/60 font-grotesk">
                                Recording… release to send
                              </span>
                              <div className="flex-1 h-1.5 rounded-full bg-base-300 overflow-hidden ml-2">
                                <div
                                  className="h-full bg-red-500 transition-all duration-75"
                                  style={{
                                    width: `${Math.max(6, audioLevel * 100)}%`,
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Processing indicator */}
                        {isProcessingAnyInput && (
                          <div className="flex items-center justify-center gap-3 py-4">
                            <div className="h-6 w-6 rounded-full border-2 border-base-300 border-t-base-content animate-spin" />
                            <span className="text-sm text-base-content/60 font-grotesk">
                              {isPreparingActivities
                                ? "Checking suggested exercises..."
                                : isTranscribingAudio
                                  ? "Processing audio..."
                                  : isProcessingImage
                                    ? "Analyzing image..."
                                    : "Building your session..."}
                            </span>
                          </div>
                        )}

                        {error && (
                          <p
                            className="text-sm text-red-500 text-center"
                            role="alert"
                          >
                            {error}
                          </p>
                        )}
                      </div>
                    )}

                    {isPremium && currentStep === "review" && (
                      <div className="space-y-3 px-4">
                        {suggestedActivities.length === 0 ? (
                          <div className="rounded-xl border border-base-300 p-4">
                            <p className="text-sm text-base-content/70">
                              No suggested exercises found. Continue to
                              generate your session.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {suggestedActivities.map((activity) => {
                              const variableChips = parseVariableNamesInput(
                                activity.variableNames,
                              );
                              return (
                                <div
                                  key={activity.id}
                                  onClick={() =>
                                    updateSuggestedActivity(activity.id, {
                                      accepted: !activity.accepted,
                                    })
                                  }
                                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                    activity.accepted
                                      ? "bg-blue-500/10 border-blue-200 hover:bg-blue-500/15"
                                      : "bg-base-200 border-base-300 hover:bg-base-300"
                                  }`}
                                >
                                  <div className="flex items-center gap-3 min-w-0 flex-1">
                                    <div
                                      className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                                        activity.accepted
                                          ? "bg-blue-500 text-white"
                                          : "bg-base-300"
                                      }`}
                                    >
                                      {activity.accepted && (
                                        <svg
                                          className="w-3 h-3"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-semibold text-base-content font-heading truncate">
                                        {activity.name}
                                      </p>
                                      <p className="text-xs text-base-content/60 font-grotesk mt-0.5 truncate">
                                        Similar to:{" "}
                                        {activity.similarTo || "None"}
                                      </p>
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {variableChips.length > 0 ? (
                                          variableChips.map((variable) => (
                                            <span
                                              key={`${activity.id}-${variable}`}
                                              className="px-2 py-0.5 bg-base-200 text-base-content/80 text-xs font-medium rounded font-grotesk"
                                            >
                                              {variable}
                                            </span>
                                          ))
                                        ) : (
                                          <span className="text-xs text-base-content/60 font-grotesk">
                                            No variables
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleOpenSuggestedActivityEditor(
                                        activity,
                                      );
                                    }}
                                    className="text-xs text-blue-600 hover:text-blue-700 ml-2 font-grotesk font-semibold px-2 py-1 rounded hover:bg-blue-500/10 transition-colors"
                                  >
                                    Edit
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={handleSkipSuggestedActivities}
                            disabled={isSavingSuggestedActivities}
                            className="flex-1 py-3 rounded-xl bg-base-200 text-base-content font-grotesk font-semibold hover:bg-base-300 transition-colors disabled:opacity-50"
                          >
                            Skip and Generate
                          </button>
                          <button
                            onClick={handleSaveSuggestedActivitiesAndContinue}
                            disabled={isSavingSuggestedActivities}
                            className="flex-1 py-3 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 text-white font-grotesk font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                          >
                            {isSavingSuggestedActivities
                              ? "Saving..."
                              : "Save Selected and Generate"}
                          </button>
                        </div>
                      </div>
                    )}

                    {editingSuggestedActivityId && (
                      <div
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[80] p-4"
                        onClick={handleCloseSuggestedActivityEditor}
                      >
                        <div
                          className="bg-base-100 rounded-xl shadow-xl w-full max-w-md p-6 space-y-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <h3 className="text-lg font-semibold text-base-content font-heading">
                            Edit Activity
                          </h3>

                          <label className="flex items-center gap-2 text-sm font-medium text-base-content">
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm"
                              checked={editSuggestedAccepted}
                              onChange={(e) =>
                                setEditSuggestedAccepted(e.target.checked)
                              }
                              disabled={isSavingSuggestedActivities}
                            />
                            Add this activity
                          </label>

                          <div className="new-form-group-rounded">
                            <input
                              type="text"
                              value={editSuggestedName}
                              onChange={(e) =>
                                setEditSuggestedName(e.target.value)
                              }
                              placeholder=" "
                              className="form-element-rounded"
                              disabled={isSavingSuggestedActivities}
                            />
                            <label>Exercise Name</label>
                          </div>

                          <div className="new-form-group-rounded">
                            <textarea
                              value={editSuggestedDescription}
                              onChange={(e) =>
                                setEditSuggestedDescription(e.target.value)
                              }
                              placeholder=" "
                              rows={3}
                              className="form-element-rounded resize-none"
                              disabled={isSavingSuggestedActivities}
                            />
                            <label>Description</label>
                          </div>

                          <div className="new-form-group-rounded">
                            <input
                              type="text"
                              value={editSuggestedVariables}
                              onChange={(e) =>
                                setEditSuggestedVariables(e.target.value)
                              }
                              placeholder=" "
                              className="form-element-rounded"
                              disabled={isSavingSuggestedActivities}
                            />
                            <label>Variables (comma separated)</label>
                          </div>

                          <div className="new-form-group-rounded">
                            <input
                              type="text"
                              value={editSuggestedSimilarTo}
                              onChange={(e) =>
                                setEditSuggestedSimilarTo(e.target.value)
                              }
                              placeholder=" "
                              className="form-element-rounded"
                              disabled={isSavingSuggestedActivities}
                            />
                            <label>Similar to (optional)</label>
                          </div>

                          <div className="flex justify-end gap-2 pt-2">
                            <button
                              onClick={handleCloseSuggestedActivityEditor}
                              className="px-4 py-2 rounded-lg bg-base-200 text-base-content font-grotesk font-medium"
                              disabled={isSavingSuggestedActivities}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveSuggestedActivityEditor}
                              className="px-4 py-2 rounded-lg bg-blue-600 text-white font-grotesk font-medium disabled:opacity-50"
                              disabled={
                                isSavingSuggestedActivities ||
                                !editSuggestedName.trim()
                              }
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 2: Processing - only show fallback if no activeInputType */}
                    {isPremium && currentStep === "processing" &&
                      activeInputType === null && (
                        <div className="flex flex-col items-center justify-center py-12">
                          <div className="relative">
                            <div className="h-20 w-20 rounded-full bg-white flex items-center justify-center animate-pulse">
                              <img
                                src={peakIconBlack}
                                alt="Peak"
                                className="h-12 w-12"
                              />
                            </div>
                            <div className="absolute inset-0 h-20 w-20 rounded-full border-4 border-purple-200 border-t-purple-600 animate-spin" />
                          </div>
                          <p className="mt-6 text-gray-600 font-grotesk">
                            AI is building your session...
                          </p>
                          <p className="mt-2 text-sm text-gray-400">
                            This may take a few seconds
                          </p>
                        </div>
                      )}

                    {/* Step 3: Complete (success message before closing) */}
                    {isPremium && currentStep === "complete" && (
                      <div className="flex flex-col items-center justify-center py-12">
                        <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
                          <CheckCircleIcon className="h-12 w-12 text-green-600" />
                        </div>
                        <p className="mt-6 text-lg font-semibold text-gray-900 font-grotesk">
                          Session Created!
                        </p>
                        <p className="mt-2 text-sm text-gray-500">
                          Review and save your session template
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Step Progress Indicator */}
                  {isPremium && (
                    <div className="flex items-center justify-center gap-2 pt-4 pb-10 px-6">
                      {(
                        ["input", "review", "processing", "complete"] as const
                      ).map((step, index) => (
                        <div
                          key={step}
                          className={`h-2 w-2 rounded-full transition-all duration-300 ${
                            currentStep === step
                              ? "bg-black w-6"
                              : [
                                    "input",
                                    "review",
                                    "processing",
                                    "complete",
                                  ].indexOf(currentStep) > index
                                ? "bg-black"
                                : "bg-gray-200"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default SessionBuilder;
